import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import Footer from '../components/Footer';

const Container = styled.div`
  background: #ffffff;
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #1a3518 0%, #2d5a27 50%, #3d7a35 100%);
  color: white;
  padding: 64px 16px 72px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
  }

  @media (min-width: 768px) {
    padding: 80px 24px 96px;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroBadge = styled.div`
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const MainTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: 800;
  margin: 0 0 16px 0;
  letter-spacing: -0.03em;
  line-height: 1.15;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const Tagline = styled.p`
  font-size: 1.05rem;
  margin: 0 0 32px 0;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 768px) {
    font-size: 1.15rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  background: #ffffff;
  color: #2d5a27;
  border: none;
  padding: 14px 32px;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.4);
  padding: 12px 28px;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.6);
  }
`;

const StatsBar = styled.div`
  background: #f8f9fa;
  padding: 24px 16px;
  border-bottom: 1px solid #eee;
`;

const StatsContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d5a27;
  margin-bottom: 4px;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.section`
  padding: 64px 16px;
  max-width: 1100px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 80px 24px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const SectionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #2d5a27;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 0.95rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ProductGrid = styled.div`
  display: grid;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ProductCard = styled.div`
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  padding: 28px 24px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2d5a27;
    box-shadow: 0 8px 24px rgba(45, 90, 39, 0.1);
    transform: translateY(-2px);
  }
`;

const ProductIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #2d5a27 0%, #1a3518 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  margin-bottom: 16px;
`;

const ProductTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px 0;
`;

const ProductDescription = styled.p`
  font-size: 0.85rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const DifferentiatorSection = styled.section`
  background: linear-gradient(135deg, #1a3518 0%, #2d5a27 100%);
  color: white;
  padding: 64px 16px;

  @media (min-width: 768px) {
    padding: 80px 24px;
  }
`;

const DiffContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  gap: 40px;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 64px;
    align-items: center;
  }
`;

const DiffText = styled.div``;

const DiffTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 16px 0;
  letter-spacing: -0.02em;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const DiffSubtitle = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  margin: 0 0 24px 0;
`;

const DiffList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DiffItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);

  &:before {
    content: '✓';
    color: #90EE90;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const DiffCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 28px;
  backdrop-filter: blur(10px);
`;

const DiffCardTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const DiffCardItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 0.85rem;

  &:last-child {
    border-bottom: none;
  }
`;

const HowItWorksSection = styled.section`
  background: #f8f9fa;
  padding: 64px 16px;

  @media (min-width: 768px) {
    padding: 80px 24px;
  }
`;

const HowItWorksContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const StepsGrid = styled.div`
  display: grid;
  gap: 24px;
  margin-top: 40px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StepCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  position: relative;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #2d5a27 0%, #1a3518 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 auto 16px auto;
`;

const StepTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px 0;
`;

const StepDescription = styled.p`
  font-size: 0.85rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const VerificationNote = styled.div`
  background: white;
  border-left: 4px solid #2d5a27;
  padding: 20px 24px;
  margin-top: 32px;
  border-radius: 0 8px 8px 0;
`;

const VerificationTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px 0;
`;

const VerificationText = styled.p`
  font-size: 0.85rem;
  color: #555;
  line-height: 1.6;
  margin: 0;
`;

const FeatureGrid = styled.div`
  display: grid;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FeatureCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 24px 20px;
  text-align: center;
`;

const FeatureIcon = styled.div`
  width: 36px;
  height: 36px;
  background: #2d5a27;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 12px;
`;

const FeatureTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 6px 0;
`;

const FeatureText = styled.p`
  font-size: 0.8rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const TestimonialSection = styled.section`
  background: #f8f9fa;
  padding: 64px 16px;

  @media (min-width: 768px) {
    padding: 80px 24px;
  }
`;

const TestimonialGrid = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TestimonialCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 28px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const TestimonialText = styled.p`
  font-size: 0.9rem;
  color: #444;
  line-height: 1.7;
  margin: 0 0 16px 0;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a2e;
`;

const TestimonialRole = styled.div`
  font-size: 0.75rem;
  color: #666;
`;

const CTASection = styled.section`
  padding: 64px 16px;
  text-align: center;
  background: white;

  @media (min-width: 768px) {
    padding: 80px 24px;
  }
`;

const CTAContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 12px 0;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const CTAText = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0 0 28px 0;
  line-height: 1.6;
`;

const CTAButton = styled.button`
  background: #2d5a27;
  color: white;
  border: none;
  padding: 14px 36px;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1f3d1b;
    transform: translateY(-1px);
  }
`;

const ShowingBookerLanding = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/register');
  };

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <HeroBadge>Built for Compliance-First Professionals</HeroBadge>
          <MainTitle>The Industry's Leading Showing Management Platform for Occupied Properties</MainTitle>
          <Tagline>
            Automate scheduling, deliver legally compliant notices, and maintain complete audit trails.
            The only platform purpose-built for tenant-occupied property showings.
          </Tagline>
          <HeroButtons>
            <PrimaryButton onClick={handleGetStarted}>Request Access</PrimaryButton>
            <SecondaryButton to="#how-it-works" onClick={(e) => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }}>See How It Works</SecondaryButton>
          </HeroButtons>
        </HeroContent>
      </HeroSection>

      <StatsBar>
        <StatsContent>
          <StatItem>
            <StatNumber>24hr</StatNumber>
            <StatLabel>Automated Notices</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>100%</StatNumber>
            <StatLabel>Compliance Coverage</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>3</StatNumber>
            <StatLabel>Delivery Channels</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>7yr</StatNumber>
            <StatLabel>Record Retention</StatLabel>
          </StatItem>
        </StatsContent>
      </StatsBar>

      <Section>
        <SectionHeader>
          <SectionLabel>Platform</SectionLabel>
          <SectionTitle>Complete Showing Management Suite</SectionTitle>
          <SectionSubtitle>
            Everything you need to manage showings professionally while staying legally compliant.
          </SectionSubtitle>
        </SectionHeader>

        <ProductGrid>
          <ProductCard>
            <ProductIcon>S</ProductIcon>
            <ProductTitle>Showing Scheduler</ProductTitle>
            <ProductDescription>
              Intelligent scheduling with automatic conflict detection, calendar sync, and mobile access for agents on the go.
            </ProductDescription>
          </ProductCard>

          <ProductCard>
            <ProductIcon>C</ProductIcon>
            <ProductTitle>Compliance Manager</ProductTitle>
            <ProductDescription>
              Automated 24/48-hour tenant notices compliant with California Civil Code 1954 and local housing regulations.
            </ProductDescription>
          </ProductCard>

          <ProductCard>
            <ProductIcon>M</ProductIcon>
            <ProductTitle>Multi-Channel Delivery</ProductTitle>
            <ProductDescription>
              SMS, email, and certified mail notifications with delivery confirmation and timestamped records.
            </ProductDescription>
          </ProductCard>

          <ProductCard>
            <ProductIcon>A</ProductIcon>
            <ProductTitle>Analytics & Insights</ProductTitle>
            <ProductDescription>
              Track showing activity, response rates, and property performance with exportable reports.
            </ProductDescription>
          </ProductCard>

          <ProductCard>
            <ProductIcon>D</ProductIcon>
            <ProductTitle>Document Generation</ProductTitle>
            <ProductDescription>
              Professional PDF compliance reports, notice templates, and complete audit documentation.
            </ProductDescription>
          </ProductCard>

          <ProductCard>
            <ProductIcon>T</ProductIcon>
            <ProductTitle>Team Management</ProductTitle>
            <ProductDescription>
              Coordinate across your brokerage with role-based access, team calendars, and centralized oversight.
            </ProductDescription>
          </ProductCard>
        </ProductGrid>
      </Section>

      <HowItWorksSection id="how-it-works">
        <HowItWorksContent>
          <SectionHeader>
            <SectionLabel>Getting Started</SectionLabel>
            <SectionTitle>How It Works</SectionTitle>
            <SectionSubtitle>
              From signup to your first showing...our streamlined process gets you managing properties in no time.
            </SectionSubtitle>
          </SectionHeader>

          <StepsGrid>
            <StepCard>
              <StepNumber>1</StepNumber>
              <StepTitle>Request Access</StepTitle>
              <StepDescription>
                Submit your information including property details. We verify ownership to protect the platform.
              </StepDescription>
            </StepCard>

            <StepCard>
              <StepNumber>2</StepNumber>
              <StepTitle>Get Verified</StepTitle>
              <StepDescription>
                Our team reviews your application and verifies property ownership within 48 hours.
              </StepDescription>
            </StepCard>

            <StepCard>
              <StepNumber>3</StepNumber>
              <StepTitle>Add Properties</StepTitle>
              <StepDescription>
                Once approved, add your tenant-occupied properties with tenant contact information.
              </StepDescription>
            </StepCard>

            <StepCard>
              <StepNumber>4</StepNumber>
              <StepTitle>Schedule Showings</StepTitle>
              <StepDescription>
                Book showings and let ShowingBooker handle notices, compliance, and documentation.
              </StepDescription>
            </StepCard>
          </StepsGrid>

          <VerificationNote>
            <VerificationTitle>Why We Verify Accounts</VerificationTitle>
            <VerificationText>
              ShowingBooker is designed exclusively for legitimate property owners, managers, and landlords.
              Our verification process protects tenants from harassment, ensures platform integrity, and
              maintains the professional standards our users expect. We verify property ownership through
              public records and contact information before granting access. This extra step means you're
              working alongside verified professionals who share your commitment to compliance.
            </VerificationText>
          </VerificationNote>
        </HowItWorksContent>
      </HowItWorksSection>

      <DifferentiatorSection>
        <DiffContent>
          <DiffText>
            <DiffTitle>Built for What Others Ignore: Occupied Properties</DiffTitle>
            <DiffSubtitle>
              Generic showing tools focus on vacant properties. We built ShowingBooker specifically
              for the complex reality of tenant-occupied showings where legal compliance isn't optional.
            </DiffSubtitle>
            <DiffList>
              <DiffItem>Automated legal notice delivery with proof of service</DiffItem>
              <DiffItem>California Civil Code 1954 compliance built-in</DiffItem>
              <DiffItem>LA Housing Department audit-ready documentation</DiffItem>
              <DiffItem>Tenant rights protection with clear communication</DiffItem>
              <DiffItem>Complete liability protection with timestamped records</DiffItem>
              <DiffItem>Certified mail integration for difficult situations</DiffItem>
            </DiffList>
          </DiffText>

          <DiffCard>
            <DiffCardTitle>Compliance Comparison</DiffCardTitle>
            <DiffCardItem>
              <span>Legal Notice Automation</span>
              <span style={{ color: '#90EE90' }}>✓ ShowingBooker</span>
            </DiffCardItem>
            <DiffCardItem>
              <span>Multi-Channel Delivery</span>
              <span style={{ color: '#90EE90' }}>✓ ShowingBooker</span>
            </DiffCardItem>
            <DiffCardItem>
              <span>Audit Trail Generation</span>
              <span style={{ color: '#90EE90' }}>✓ ShowingBooker</span>
            </DiffCardItem>
            <DiffCardItem>
              <span>Certified Mail Integration</span>
              <span style={{ color: '#90EE90' }}>✓ ShowingBooker</span>
            </DiffCardItem>
            <DiffCardItem>
              <span>LAHD Compliance</span>
              <span style={{ color: '#90EE90' }}>✓ ShowingBooker</span>
            </DiffCardItem>
          </DiffCard>
        </DiffContent>
      </DifferentiatorSection>

      <Section>
        <SectionHeader>
          <SectionLabel>Features</SectionLabel>
          <SectionTitle>Professional Tools for Real Estate Professionals</SectionTitle>
          <SectionSubtitle>
            Everything you need to manage showings efficiently and professionally.
          </SectionSubtitle>
        </SectionHeader>

        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon>+</FeatureIcon>
            <FeatureTitle>Mobile Access</FeatureTitle>
            <FeatureText>Manage showings from anywhere with our responsive platform.</FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>+</FeatureIcon>
            <FeatureTitle>Calendar Sync</FeatureTitle>
            <FeatureText>Integrate with Google, Outlook, and Apple calendars.</FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>+</FeatureIcon>
            <FeatureTitle>Instant Notifications</FeatureTitle>
            <FeatureText>Real-time alerts for bookings, changes, and confirmations.</FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>+</FeatureIcon>
            <FeatureTitle>Enterprise Security</FeatureTitle>
            <FeatureText>Bank-level encryption and secure data handling.</FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>+</FeatureIcon>
            <FeatureTitle>Custom Templates</FeatureTitle>
            <FeatureText>Personalize notices with your branding and messaging.</FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>+</FeatureIcon>
            <FeatureTitle>Performance Reports</FeatureTitle>
            <FeatureText>Track metrics and demonstrate value to clients.</FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>+</FeatureIcon>
            <FeatureTitle>Brokerage Tools</FeatureTitle>
            <FeatureText>Centralized management for multi-agent teams.</FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>+</FeatureIcon>
            <FeatureTitle>API Access</FeatureTitle>
            <FeatureText>Integrate with your existing tech stack.</FeatureText>
          </FeatureCard>
        </FeatureGrid>
      </Section>

      <TestimonialSection>
        <SectionHeader>
          <SectionLabel>Trusted By Professionals</SectionLabel>
          <SectionTitle>What Our Users Say</SectionTitle>
        </SectionHeader>

        <TestimonialGrid>
          <TestimonialCard>
            <TestimonialText>
              "Finally, a platform that understands the complexity of showing occupied units.
              The compliance documentation alone has saved me from potential legal issues."
            </TestimonialText>
            <TestimonialAuthor>Property Manager</TestimonialAuthor>
            <TestimonialRole>Los Angeles, CA</TestimonialRole>
          </TestimonialCard>

          <TestimonialCard>
            <TestimonialText>
              "The automated 24-hour notices are a game changer. I used to spend hours coordinating
              with tenants. Now it's all handled automatically."
            </TestimonialText>
            <TestimonialAuthor>Real Estate Agent</TestimonialAuthor>
            <TestimonialRole>San Francisco, CA</TestimonialRole>
          </TestimonialCard>

          <TestimonialCard>
            <TestimonialText>
              "When a tenant complained to the housing department, I had a complete audit trail
              showing proper notice. That documentation was invaluable."
            </TestimonialText>
            <TestimonialAuthor>Landlord</TestimonialAuthor>
            <TestimonialRole>San Diego, CA</TestimonialRole>
          </TestimonialCard>
        </TestimonialGrid>
      </TestimonialSection>

      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Professionalize Your Showing Process?</CTATitle>
          <CTAText>
            Join thousands of real estate professionals who trust ShowingBooker for compliant,
            efficient, and documented showing management.
          </CTAText>
          <CTAButton onClick={handleGetStarted}>Request Access</CTAButton>
        </CTAContent>
      </CTASection>

      <Footer />
    </Container>
  );
};

export default ShowingBookerLanding;
